/**
 * The application shell.
 *
 * **The chrome is shared and whole** (FiveOX ADR-0037). `<PlatformShell>` is
 * a top bar, a left sidebar and the content between them; this app renders it
 * and composes nothing of its own around it. In particular:
 *
 * - The nav bar carries **no app navigation**. It is suite chrome: brand,
 *   search, org, notifications, app-switcher waffle, account menu. Search is
 *   the one app-owned thing in it, and it arrives through the shell's `search`
 *   slot rather than as markup of ours around the bar.
 * - ProDesk's navigation goes in the **sidebar, as data** — `NavItem[]` with at
 *   most one level of `children`.
 * - The bell is the shell's, mounted with **our** authenticated fetcher, not a
 *   token: each app owns a different session store with its own idea of what a
 *   dead session means, and the bell must not become one more place that
 *   decides.
 * - **No router import inside the package.** `renderLink` wraps each item in
 *   react-router's `<Link>`; without one the sidebar renders plain anchors.
 */

import {
  NoAccess,
  Notifications,
  PlatformShell,
  navItemClass,
  signOutEverywhere,
  subNavItemClass,
  useSwitcherApps,
} from '@fiveox/shell'
import type { NavItem } from '@fiveox/shell'
import { BriefcaseBusiness, IndianRupee, Landmark, Users } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router'

import {
  BOOKING,
  CALENDAR,
  CASE,
  CLIENT,
  IMPORT,
  INVOICE,
  LEAD,
  LEDGER,
  PERSON,
  REPORT,
  SERVICE,
  SETTINGS,
  SITE,
  useEvaluation,
} from './lib/access'
import type { Verb } from './lib/access'
import { getOrgId, setOrgId } from './lib/api'
import { useOrgs } from './lib/use-orgs'
import {
  ACCESS_API,
  ACCOUNTS_URL,
  ADMIN_URL,
  APP_NAME,
  APP_SLUG,
  APPMGMT_API,
  AUTH_API,
  COMM_API,
  CONSOLE_URL,
  CRM_URL,
  HELP_URL,
  LAUNCHER_URL,
  STORE_URL,
  SUPPORT_URL,
} from './lib/config'
import { useMe } from './lib/use-me'
import { session } from './lib/session'
import { SearchBox } from './components/SearchBox'
import { Paywall } from './routes/Paywall'

/**
 * The sidebar, as data.
 *
 * `needs` is ours, not the shell's: `[resource, verb]`, matching the matrix
 * access-management actually speaks. An item whose verb the user does not hold
 * is **dropped**, not disabled — a disabled item advertises a capability the
 * user cannot obtain and generates a support ticket.
 *
 * **Grouped since Wave 15** (C14 §10.6 item 5, ADR-0033 §3): Work · Clients ·
 * Money · Firm, through the one level of `children` `@fiveox/shell` draws. A
 * group has no verb of its own — it is dropped when every child is, and its row
 * opens its first visible child (the shell draws a group's row as a link, so it
 * needs an `href`, and a fixed one would point some roles at a page they
 * cannot open). The shell opens only the group holding the current page.
 *
 * Every entry below is a real route. A new one is one entry here, one in
 * `router.tsx`, and a resource that is **already declared** in
 * `services/prodesk-service/app/rbac.py` — a resource you find missing is a
 * change to spec §P, not a local edit. See docs/DOMAIN_HANDOFF.md §6.
 *
 * Why each entry needs what it needs (kept from the flat rail it replaced):
 * - **Overview** needs nothing: every member has a day.
 * - **Cases**, **Workload**, **Messages** — `case:can_view`, the *narrow* verb:
 *   an associate holds it without `can_view_all` and sees their own work
 *   (FR-L4, narrowed in `CaseRepo`), not a hidden entry. Messages hang off a
 *   case. There is no Documents entry: a case's documents live under the case.
 * - **Calendar** — `calendar:can_view`; the reviewer, associate and article
 *   assistant hold none (what a firm files on a schedule is a partner's call).
 * - **Clients** — `client:can_view`. **People** — `person:can_view` (an
 *   associate views and does nothing more, spec §B).
 * - **Leads** and **Proposals** — `lead:can_view`; a proposal is governed by
 *   `lead` because `proposal` is not one of the fourteen resources T-1.4
 *   publishes. An associate sees only leads they own or nobody has taken.
 * - **Invoices** and **Retainers** — `invoice:can_view` (a retainer is an
 *   invoice that has not happened yet). **Receipts** and **Statement** —
 *   `ledger:can_view`, the verb the Invoices page gated its links to them on
 *   before they had rail rows of their own.
 * - **Services** — `service:can_view` (partner, practice manager, viewer).
 * - **Website** — `site:can_view` (partner, practice manager).
 * - **Broadcasts** — `client:can_view_all`: a broadcast is composed from the
 *   firm's whole client list, so an associate never sees it.
 * - **Communication** and **Settings** — `settings:can_view`, as the service
 *   gates them.
 * - **Reports** — `report:can_view`; the service then lists only the reports
 *   a caller's other verbs open.
 * - **Imports** — `import:can_view` (partner, practice manager, viewer).
 * - **Bookings** — `booking:can_view` (partner, practice manager, viewer;
 *   C19, ADR-0037 §6). Its flows are edited under Website.
 */
type NavEntry = NavItem & { needs?: [string, Verb] }

/** A labelled group of entries. No `href`: it takes its first visible child's. */
export interface NavGroup {
  key: string
  label: string
  icon: ReactNode
  children: NavEntry[]
}

export const NAV: Array<NavEntry | NavGroup> = [
  {
    key: 'group-work',
    label: 'Work',
    icon: <BriefcaseBusiness size={18} />,
    children: [
      { key: 'overview', label: 'Overview', href: '/', end: true },
      { key: 'cases', label: 'Cases', href: '/cases', needs: [CASE, 'can_view'] },
      { key: 'calendar', label: 'Calendar', href: '/calendar', needs: [CALENDAR, 'can_view'] },
      { key: 'workload', label: 'Workload', href: '/workload', needs: [CASE, 'can_view'] },
      { key: 'messages', label: 'Messages', href: '/messages', needs: [CASE, 'can_view'] },
    ],
  },
  {
    key: 'group-clients',
    label: 'Clients',
    icon: <Users size={18} />,
    children: [
      { key: 'clients', label: 'Clients', href: '/clients', needs: [CLIENT, 'can_view'] },
      { key: 'people', label: 'People', href: '/people', needs: [PERSON, 'can_view'] },
      { key: 'leads', label: 'Leads', href: '/leads', needs: [LEAD, 'can_view'] },
      { key: 'proposals', label: 'Proposals', href: '/proposals', needs: [LEAD, 'can_view'] },
    ],
  },
  {
    key: 'group-money',
    label: 'Money',
    icon: <IndianRupee size={18} />,
    children: [
      { key: 'billing', label: 'Invoices', href: '/billing', needs: [INVOICE, 'can_view'] },
      { key: 'receipts', label: 'Receipts', href: '/billing/receipts', needs: [LEDGER, 'can_view'] },
      { key: 'statement', label: 'Statement', href: '/billing/ledger', needs: [LEDGER, 'can_view'] },
      {
        key: 'retainers',
        label: 'Retainers',
        href: '/billing/retainers',
        needs: [INVOICE, 'can_view'],
      },
      // w19b — C19, FR-J11 item 9, ADR-0037 §6.
      { key: 'bookings', label: 'Bookings', href: '/bookings', needs: [BOOKING, 'can_view'] },
    ],
  },
  {
    key: 'group-firm',
    label: 'Firm',
    icon: <Landmark size={18} />,
    children: [
      { key: 'services', label: 'Services', href: '/services', needs: [SERVICE, 'can_view'] },
      { key: 'site', label: 'Website', href: '/site', needs: [SITE, 'can_view'] },
      {
        key: 'broadcasts',
        label: 'Broadcasts',
        href: '/broadcasts',
        needs: [CLIENT, 'can_view_all'],
      },
      { key: 'comms', label: 'Communication', href: '/comms', needs: [SETTINGS, 'can_view'] },
      { key: 'reports', label: 'Reports', href: '/reports', needs: [REPORT, 'can_view'] },
      { key: 'imports', label: 'Imports', href: '/imports', needs: [IMPORT, 'can_view'] },
      { key: 'settings', label: 'Settings', href: '/settings', needs: [SETTINGS, 'can_view'] },
    ],
  },
]

function isGroup(entry: NavEntry | NavGroup): entry is NavGroup {
  return 'children' in entry && Array.isArray(entry.children) && !('href' in entry)
}

/** The rail a caller sees: every entry whose verb they hold, every group with
 *  at least one such entry, a group's row opening its first visible child. */
export function visibleNav(
  nav: Array<NavEntry | NavGroup>,
  can: (resource: string, verb: Verb) => boolean,
): NavItem[] {
  const allowed = (entry: NavEntry) => !entry.needs || can(entry.needs[0], entry.needs[1])
  const strip = ({ needs: _needs, ...item }: NavEntry): NavItem => item
  const out: NavItem[] = []
  for (const entry of nav) {
    if (isGroup(entry)) {
      const children = entry.children.filter(allowed).map(strip)
      if (children.length === 0) continue
      out.push({ key: entry.key, label: entry.label, icon: entry.icon, href: children[0]!.href, children })
    } else if (allowed(entry)) {
      out.push(strip(entry))
    }
  }
  return out
}

/** Which entry the current page belongs to: the longest `href` the path is at
 *  or inside, by whole segment — `/billing/invoices/7` is Invoices, and
 *  `/billing/receipts` is Receipts, not Invoices. `/` claims only itself. */
export function activeNavKey(items: NavItem[], path: string): string | null {
  let best: { key: string; length: number } | null = null
  const leaves = items.flatMap((item) => (item.children?.length ? item.children : [item]))
  for (const leaf of leaves) {
    const claims =
      leaf.href === '/' ? path === '/' : path === leaf.href || path.startsWith(`${leaf.href}/`)
    if (claims && (!best || leaf.href.length > best.length)) {
      best = { key: leaf.key, length: leaf.href.length }
    }
  }
  return best?.key ?? null
}

export function App({ children }: { children: ReactNode }) {
  const current = session.useSession()
  const location = useLocation()
  const me = useMe()
  // Resolve the org *before* anything asks for permissions: every request to
  // prodesk-service carries it, and `useEvaluation` is one of them.
  const { orgs, selected, loading: orgsLoading, failed: orgsFailed } = useOrgs()
  const { evaluation, loading, can } = useEvaluation()

  // **The waffle is identity-shaped, not app-shaped** (ADR-0054): an internal
  // caller is offered the internal apps their platform-scoped assignments
  // grant; an external caller gets the customer estate. One resolver, all apps.
  //
  // This used to be a hardcoded three-entry literal with our own wording —
  // "Console" where the estate says "Organisation", "Help" where it says
  // "Support" — and `accounts` missing altogether. `platformApps` owns the
  // canonical names and order; entries with no URL are dropped rather than
  // rendered dead.
  //
  // **`appmgmtApiUrl` + `orgId` are what put the org's own products in it.**
  // Without them an external caller's waffle was Accounts, Organisation, Store
  // and Support — four platform surfaces and not one of the apps the firm pays
  // for, so the one control whose whole job is "where can I go" could not name
  // ProDesk, TDS Reco or anything else the subscription holds. The shell reads
  // `GET /api/apps/me/apps?org=…`, which establishes membership of the org
  // before it answers, and renders only the rows the server marks
  // `launchable` — owned **and** with an address in this environment. An owned
  // app with nowhere to go belongs on the launcher's dashboard, which reports
  // what you hold; a switcher must not offer a door with no room behind it.
  //
  // `fetcher` rather than a bare token, for the reason the bell takes one: it
  // refreshes a spent access token and replays, and this app's session store is
  // the only thing that knows what a dead session means here.
  const switcherApps = useSwitcherApps({
    accessApiUrl: ACCESS_API,
    accessToken: current?.accessToken ?? null,
    staffUrls: { 'core-admin': ADMIN_URL, 'core-crm': CRM_URL, 'core-support': SUPPORT_URL },
    platformUrls: {
      accounts: ACCOUNTS_URL,
      console: CONSOLE_URL,
      store: STORE_URL,
      help: HELP_URL,
    },
    accountsUrl: ACCOUNTS_URL,
    appmgmtApiUrl: APPMGMT_API,
    // Null until `useOrgs` resolves, which is a real state on first paint. The
    // shell holds the empty list until then rather than reading the wrong org.
    orgId: selected?.id ?? null,
    fetcher: session.authed,
  })

  const signOut = () => {
    void signOutEverywhere({
      authApiUrl: AUTH_API,
      refreshToken: current?.refreshToken ?? null,
      clearLocal: session.clearSession,
    })
  }

  if (orgsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading your organisations…</p>
      </div>
    )
  }

  // Org-service did not answer. **Not** the same as belonging to no org, and
  // the copy must not say it is — telling someone they have no organisation
  // when the service is merely down sends them to support with the wrong
  // question.
  if (orgsFailed) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="We can’t reach your organisations"
        message="The organisation service did not answer. This is usually brief — please try again in a moment."
      />
    )
  }

  // Genuinely a member of nothing. A real state for a brand-new account, and
  // the only way out is to create or join one — which is accounts' job, not
  // ours.
  if (!selected || !getOrgId()) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="You’re not in an organisation yet"
        message="ProDesk works inside an organisation — that is who holds the subscription and the client list. Create one from your account, then come back."
      />
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking your access…</p>
      </div>
    )
  }

  // An empty verb set is the single answer for every lapse — never bought,
  // subscription suspended, app killed, authority unreachable. Enforcing
  // `permissions` alone is entitlement-correct (guide §7.3), and the *reason*
  // is what lets the screen say why.
  if (!evaluation || Object.keys(evaluation.permissions).length === 0) {
    return (
      <Paywall
        reason={evaluation?.entitlementReason ?? 'authority_unreachable'}
        orgs={orgs}
        selectedOrgId={selected.id}
        onSelectOrg={setOrgId}
      />
    )
  }

  const items = visibleNav(NAV, can)
  const activeKey = activeNavKey(items, location.pathname)

  return (
    <PlatformShell
      appName={APP_NAME}
      appSlug={APP_SLUG}
      launcherUrl={LAUNCHER_URL}
      account={{
        name: me?.name ?? null,
        email: me?.email ?? null,
        pictureUrl: me?.pictureUrl ?? null,
        accountsUrl: ACCOUNTS_URL,
        onSignOut: signOut,
      }}
      apps={switcherApps}
      // Display-only, and the bar's alone (ADR-0062 §4): a staff account holds
      // no organisation, so the bar drops the org control for one rather than
      // each host deciding. Never a permission input — that is `useEvaluation`.
      accessToken={current?.accessToken ?? null}
      // The org switcher. One prop, two behaviours: with `options` and
      // `onSelect` it switches; with only `selected` it renders a read-only
      // chip — which is what a single-org user gets, correctly.
      org={{
        selected: { id: selected.id, display_name: selected.display_name, role: selected.role },
        options: orgs.length > 1 ? orgs : undefined,
        onSelect: orgs.length > 1 ? (id: string) => setOrgId(id) : undefined,
      }}
      notifications={<Notifications commApiUrl={COMM_API} fetcher={session.authed} />}
      // FR-B6, immediately left of the org control (owner, 2026-09-13). The
      // shell shows the slot from `lg` up; below it the copy in the content
      // takes over, so a phone or a tablet keeps search.
      search={<SearchBox placement="bar" />}
      nav={{
        items,
        currentPath: location.pathname,
        // Active state is ours to decide (the shell passes the inactive class):
        // the one entry the page belongs to, by `activeNavKey`. A group's own
        // row is never marked — the shell opens it instead.
        renderLink: (item, className, depth) => {
          const active = item.key === activeKey
          const cls = item.children?.length
            ? className
            : depth === 1
              ? subNavItemClass(active)
              : navItemClass(active)
          return (
            <Link to={item.href} className={cls} aria-current={active ? 'page' : undefined}>
              {item.icon}
              {item.label}
            </Link>
          )
        },
      }}
      contentClassName="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6"
    >
      {/* w5g — FR-B6, the small-screen copy. From `lg` up the field is in the
          bar (the `search` prop above); below `lg` the bar has no room, so the box
          keeps its old place above the content. Rendered once here so every
          screen has it rather than once per screen. */}
      <SearchBox placement="content" />
      {children}
    </PlatformShell>
  )
}
